import type { StackByOption } from '../components/FilterControls';

// ============================================
// Shared Category Definitions
// ============================================

export interface LabelledField {
  id: string;
  label: string;
}

/** 14 sector fields with their cache keys and display labels */
export const SECTOR_FIELDS: LabelledField[] = [
  { id: 'sector_agriculture', label: 'Agriculture, Mining, Construction & Manufacturing' },
  { id: 'sector_trade', label: 'Trade, Transportation & Utilities' },
  { id: 'sector_information', label: 'Information' },
  { id: 'sector_finance', label: 'Finance & Insurance' },
  { id: 'sector_realestate', label: 'Real Estate, Rental & Leasing' },
  { id: 'sector_professional', label: 'Professional & Technical Services' },
  { id: 'sector_scientific', label: 'Scientific Research & Development Services' },
  { id: 'sector_management', label: 'Management, Administrative & Support Services' },
  { id: 'sector_education', label: 'Educational Services' },
  { id: 'sector_health', label: 'Health Care & Social Assistance' },
  { id: 'sector_arts', label: 'Arts, Entertainment & Recreation' },
  { id: 'sector_accommodation', label: 'Accommodation, Food & Other Services' },
  { id: 'sector_publicadmin', label: 'Public Administration (excl. National Security)' },
  { id: 'sector_nationalsecurity', label: 'National Security' },
];

/** 6 AI lifecycle stage labels */
export const LIFECYCLE_STAGES: string[] = [
  'Plan and Design',
  'Collect and Process Data',
  'Build and Use Model',
  'Operate and Monitor',
  'Deploy',
  'Verify and Validate',
];

/** 7 top-level risk domains with their subdomain column groups */
export interface RiskDomain {
  id: string;
  label: string;
  subdomainCols: string[];
}

export const RISK_DOMAINS: RiskDomain[] = [
  { id: '1', label: 'Discrimination & Toxicity', subdomainCols: ['subdomain_1_1_coverage', 'subdomain_1_2_coverage', 'subdomain_1_3_coverage'] },
  { id: '2', label: 'Privacy & Security', subdomainCols: ['subdomain_2_1_coverage', 'subdomain_2_2_coverage'] },
  { id: '3', label: 'Misinformation', subdomainCols: ['subdomain_3_1_coverage', 'subdomain_3_2_coverage'] },
  { id: '4', label: 'Malicious Actors', subdomainCols: ['subdomain_4_1_coverage', 'subdomain_4_2_coverage', 'subdomain_4_3_coverage'] },
  { id: '5', label: 'Human-Computer Interaction', subdomainCols: ['subdomain_5_1_coverage', 'subdomain_5_2_coverage'] },
  { id: '6', label: 'Socioeconomic & Environmental', subdomainCols: ['subdomain_6_1_coverage', 'subdomain_6_2_coverage', 'subdomain_6_3_coverage', 'subdomain_6_4_coverage', 'subdomain_6_5_coverage', 'subdomain_6_6_coverage'] },
  { id: '7', label: 'AI System Safety, Failures & Limitations', subdomainCols: ['subdomain_7_1_coverage', 'subdomain_7_2_coverage', 'subdomain_7_3_coverage', 'subdomain_7_4_coverage', 'subdomain_7_5_coverage', 'subdomain_7_6_coverage'] },
];

/** Actor role types for the actors chart */
export type ActorRole = 'targetTypes' | 'proposerTypes' | 'enforcerTypes' | 'monitorTypes';

export const ACTOR_ROLES: { id: ActorRole; label: string }[] = [
  { id: 'targetTypes', label: 'Target' },
  { id: 'proposerTypes', label: 'Proposer' },
  { id: 'enforcerTypes', label: 'Enforcer' },
  { id: 'monitorTypes', label: 'Monitor' },
];

export const DEFAULT_ACTOR_ROLE: ActorRole = 'targetTypes';

/** 11 technical scope term labels */
export const TECHNICAL_SCOPE_TERMS: string[] = [
  'AI Models',
  'Foundation Model',
  'Foundation Models',
  'Frontier AI',
  'AI Systems',
  'Compute Threshold',
  'General Purpose AI',
  'Generative AI',
  'Open Weight / Open Source',
  'Predictive AI',
  'Task-specific AI',
];

// ============================================
// Chart Configuration
// ============================================

export type AggregationType =
  | 'risk-subdomain'
  | 'riskDomain'
  | 'sectors'
  | 'lifecycle'
  | 'technicalScope'
  | 'legislativeStatus'
  | 'actors';

export type TooltipHeaderFormat = 'id-label' | 'label-only';

export type DetailsFilterType =
  | 'subdomain'
  | 'riskDomain'
  | 'sector'
  | 'lifecycle'
  | 'technicalScope'
  | 'legislativeStatus'
  | 'actors';

export interface ChartConfig {
  /** Unique chart type identifier used in GovCharts.init() */
  chartType: string;
  /** The aggregation param sent to the API */
  aggregation: AggregationType;
  /** Display title shown above the filter bar */
  title: string;
  /** Which stacking option is selected by default */
  defaultStackBy: StackByOption;
  /** Which stack-by buttons to show in the filter bar */
  stackByOptions: StackByOption[];
  /** Whether to show the coverage level filter dropdown */
  showCoverageFilter: boolean;
  /** Format for tooltip headers: 'id-label' shows "1.1 - Toxic content", 'label-only' shows just the label */
  tooltipHeaderFormat: TooltipHeaderFormat;
  /** Which filter type to use when calling the details API on bar click */
  detailsFilterType: DetailsFilterType;
  /** Whether y-axis labels have hover descriptions (only risk subdomains) */
  hasYAxisDescriptions: boolean;
  /** Filter dropdowns to hide because their dimension is the y-axis */
  hiddenFilters: string[];
  /** Default view mode */
  defaultViewMode: 'proportion' | 'counts';
}

// ============================================
// Chart Config Instances
// ============================================

const ALL_STACK_OPTIONS: StackByOption[] = [
  'coverage', 'jurisdiction', 'authority', 'legislativeStatus', 'activity',
];

const NON_COVERAGE_STACK_OPTIONS: StackByOption[] = [
  'jurisdiction', 'authority', 'legislativeStatus', 'activity',
];

export const RISK_DOMAIN_CONFIG: ChartConfig = {
  chartType: 'risk-domain-bar',
  aggregation: 'riskDomain',
  title: 'Risk Domain Coverage',
  defaultStackBy: 'coverage',
  stackByOptions: ALL_STACK_OPTIONS,
  showCoverageFilter: true,
  tooltipHeaderFormat: 'label-only',
  detailsFilterType: 'riskDomain',
  hasYAxisDescriptions: false,
  hiddenFilters: ['riskSubdomain'],
  defaultViewMode: 'proportion',
};

export const RISK_SUBDOMAIN_CONFIG: ChartConfig = {
  chartType: 'risk-subdomain-bar',
  aggregation: 'risk-subdomain',
  title: 'Risk Subdomain Coverage',
  defaultStackBy: 'coverage',
  stackByOptions: ALL_STACK_OPTIONS,
  showCoverageFilter: true,
  tooltipHeaderFormat: 'id-label',
  detailsFilterType: 'subdomain',
  hasYAxisDescriptions: true,
  hiddenFilters: ['riskSubdomain'],
  defaultViewMode: 'proportion',
};

export const SECTORS_COVERED_CONFIG: ChartConfig = {
  chartType: 'sectors-covered-bar',
  aggregation: 'sectors',
  title: 'Sectors Covered',
  defaultStackBy: 'coverage',
  stackByOptions: ALL_STACK_OPTIONS,
  showCoverageFilter: true,
  tooltipHeaderFormat: 'label-only',
  detailsFilterType: 'sector',
  hasYAxisDescriptions: false,
  hiddenFilters: ['sector'],
  defaultViewMode: 'proportion',
};

export const LIFECYCLE_STAGES_CONFIG: ChartConfig = {
  chartType: 'lifecycle-stages-bar',
  aggregation: 'lifecycle',
  title: 'AI Lifecycle Stages',
  defaultStackBy: 'jurisdiction',
  stackByOptions: NON_COVERAGE_STACK_OPTIONS,
  showCoverageFilter: false,
  tooltipHeaderFormat: 'label-only',
  detailsFilterType: 'lifecycle',
  hasYAxisDescriptions: false,
  hiddenFilters: ['lifecycleStage'],
  defaultViewMode: 'counts',
};

export const TECHNICAL_SCOPE_CONFIG: ChartConfig = {
  chartType: 'technical-scope-bar',
  aggregation: 'technicalScope',
  title: 'Technical Scope',
  defaultStackBy: 'jurisdiction',
  stackByOptions: NON_COVERAGE_STACK_OPTIONS,
  showCoverageFilter: false,
  tooltipHeaderFormat: 'label-only',
  detailsFilterType: 'technicalScope',
  hasYAxisDescriptions: false,
  hiddenFilters: ['technicalScope'],
  defaultViewMode: 'counts',
};

export const LEGISLATIVE_STATUS_CONFIG: ChartConfig = {
  chartType: 'legislative-status-bar',
  aggregation: 'legislativeStatus',
  title: 'Legislative Status',
  defaultStackBy: 'jurisdiction',
  stackByOptions: ['jurisdiction', 'authority', 'activity'],
  showCoverageFilter: false,
  tooltipHeaderFormat: 'label-only',
  detailsFilterType: 'legislativeStatus',
  hasYAxisDescriptions: false,
  hiddenFilters: ['legislativeStatus'],
  defaultViewMode: 'counts',
};

export const ACTORS_CONFIG: ChartConfig = {
  chartType: 'actors-bar',
  aggregation: 'actors',
  title: 'Actors',
  defaultStackBy: 'jurisdiction',
  stackByOptions: NON_COVERAGE_STACK_OPTIONS,
  showCoverageFilter: false,
  tooltipHeaderFormat: 'label-only',
  detailsFilterType: 'actors',
  hasYAxisDescriptions: false,
  hiddenFilters: [],
  defaultViewMode: 'counts',
};

// ============================================
// Incident Chart Types
// ============================================

export type IncidentChartType =
  | 'incident-domain-year'
  | 'incident-domain-subdomain'
  | 'incident-domain-pie'
  | 'incident-subdomain-year';

export interface IncidentChartConfig {
  chartType: IncidentChartType;
  /** API chartType param */
  apiChartType: string;
  title: string;
  /** 'vertical-bar' | 'horizontal-bar' | 'pie' */
  visualType: 'vertical-bar' | 'horizontal-bar' | 'pie';
}

export const INCIDENT_DOMAIN_YEAR_CONFIG: IncidentChartConfig = {
  chartType: 'incident-domain-year',
  apiChartType: 'domain-by-year',
  title: 'Risk Domain (Incident count)',
  visualType: 'vertical-bar',
};

export const INCIDENT_DOMAIN_SUBDOMAIN_CONFIG: IncidentChartConfig = {
  chartType: 'incident-domain-subdomain',
  apiChartType: 'domain-subdomain',
  title: 'Incident Count by Domain/Subdomain',
  visualType: 'horizontal-bar',
};

export const INCIDENT_DOMAIN_PIE_CONFIG: IncidentChartConfig = {
  chartType: 'incident-domain-pie',
  apiChartType: 'domain-pie',
  title: 'Risk Domain (All Incidents)',
  visualType: 'pie',
};

export const INCIDENT_SUBDOMAIN_YEAR_CONFIG: IncidentChartConfig = {
  chartType: 'incident-subdomain-year',
  apiChartType: 'subdomain-by-year',
  title: 'Risk Subdomain (Incident count)',
  visualType: 'vertical-bar',
};

export const INCIDENT_CHART_CONFIGS: Record<string, IncidentChartConfig> = {
  'incident-domain-year': INCIDENT_DOMAIN_YEAR_CONFIG,
  'incident-domain-subdomain': INCIDENT_DOMAIN_SUBDOMAIN_CONFIG,
  'incident-domain-pie': INCIDENT_DOMAIN_PIE_CONFIG,
  'incident-subdomain-year': INCIDENT_SUBDOMAIN_YEAR_CONFIG,
};

/** Look up a chart config by its chartType string */
export const CHART_CONFIGS: Record<string, ChartConfig> = {
  'risk-domain-bar': RISK_DOMAIN_CONFIG,
  'risk-subdomain-bar': RISK_SUBDOMAIN_CONFIG,
  'sectors-covered-bar': SECTORS_COVERED_CONFIG,
  'lifecycle-stages-bar': LIFECYCLE_STAGES_CONFIG,
  'technical-scope-bar': TECHNICAL_SCOPE_CONFIG,
  'legislative-status-bar': LEGISLATIVE_STATUS_CONFIG,
  'actors-bar': ACTORS_CONFIG,
};
